/**
 * Onboarding step progress indicator (APP-002 §26).
 *
 * Accessibility-first: exposes the current step + total steps as a semantic
 * ordered list with aria-current, and announces the active step. Never relies
 * on visual dots alone (APP-002 §26).
 */
import { ONBOARDING_STEPS, ONBOARDING_TOTAL_STEPS, stepOrdinal, type OnboardingStepId } from './onboardingSteps';
import { cn } from '@/lib/utils';

export interface OnboardingProgressProps {
  current: OnboardingStepId;
}

export function OnboardingProgress({ current }: OnboardingProgressProps) {
  const ordinal = stepOrdinal(current);
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground" aria-live="polite">
        Step {ordinal} of {ONBOARDING_TOTAL_STEPS}
      </p>
      <ol className="flex items-center gap-2">
        {ONBOARDING_STEPS.map((step, index) => {
          const isCurrent = step.id === current;
          const isComplete = stepOrdinal(step.id) < ordinal;
          return (
            <li key={step.id} className="flex items-center gap-2">
              <span
                aria-current={isCurrent ? 'step' : undefined}
                className={cn(
                  'flex h-6 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition-colors',
                  isCurrent && 'border-primary bg-primary text-primary-foreground',
                  isComplete && 'border-success/50 bg-success/10 text-success',
                  !isCurrent && !isComplete && 'border-border text-muted-foreground',
                )}
              >
                <span
                  className={cn(
                    'flex h-3.5 w-3.5 items-center justify-center rounded-full text-[10px]',
                    isCurrent && 'bg-primary-foreground/20',
                    isComplete && 'bg-success/20',
                  )}
                >
                  {index + 1}
                </span>
                <span className="hidden sm:inline">{step.title}</span>
              </span>
              {index < ONBOARDING_STEPS.length - 1 ? (
                <span className="h-px w-4 bg-border sm:w-6" aria-hidden="true" />
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
